import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getManagementTenantPreferencesQueryKey,
  updateManagementTenantPreferencesMutation,
  uploadManagementPortalBackgroundMutation,
} from "@harmonyso/tenants-sdk";
import { cellRequestOptions } from "@/lib/cells/cells";
import { invalidateCellTenantQueries } from "@/lib/cells/cell-queries";
import type {
  UpdateManagementTenantPreferencesResponse,
  UploadManagementPortalBackgroundResponse,
} from "@harmonyso/tenants-sdk";

interface UpdatePreferencesParams {
  tenantId: string;
  deploymentCell?: string | null;
  portalPrimaryColor?: string | null;
  portalBackgroundUrl?: string | null;
}

interface UploadBackgroundParams {
  file: File;
  tenantId: string;
  deploymentCell?: string | null;
}

function useInvalidateTenantQueries() {
  const queryClient = useQueryClient();
  return (tenantId: string, deploymentCell?: string | null) => {
    invalidateCellTenantQueries(queryClient, deploymentCell, tenantId);
    void queryClient.invalidateQueries({
      queryKey: getManagementTenantPreferencesQueryKey({
        ...cellRequestOptions(deploymentCell),
        path: { tenant_id: tenantId },
      }),
    });
  };
}

export const useManagementUpdatePreferences = () => {
  const invalidate = useInvalidateTenantQueries();

  return useMutation<
    UpdateManagementTenantPreferencesResponse,
    Error,
    UpdatePreferencesParams
  >({
    mutationFn: async ({
      tenantId,
      deploymentCell,
      portalPrimaryColor,
      portalBackgroundUrl,
    }) => {
      const mutationOptions = updateManagementTenantPreferencesMutation();
      const result = await mutationOptions.mutationFn?.({
        ...cellRequestOptions(deploymentCell),
        path: { tenant_id: tenantId },
        body: {
          portal_primary_color: portalPrimaryColor,
          portal_background_url: portalBackgroundUrl,
        },
      });

      if (!result) {
        throw new Error("Update failed - no response from server");
      }

      return result;
    },
    onSuccess: (_data, variables) => {
      toast.success("Portal branding updated successfully");
      invalidate(variables.tenantId, variables.deploymentCell);
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update portal branding. Please try again.";
      toast.error(errorMessage);
      console.error("Update preferences error:", error);
    },
  });
};

export const useManagementPortalBackgroundUpload = () => {
  const invalidate = useInvalidateTenantQueries();

  return useMutation<
    UploadManagementPortalBackgroundResponse,
    Error,
    UploadBackgroundParams
  >({
    mutationFn: async ({ file, tenantId, deploymentCell }) => {
      const mutationOptions = uploadManagementPortalBackgroundMutation();
      const result = await mutationOptions.mutationFn?.({
        ...cellRequestOptions(deploymentCell),
        path: { tenant_id: tenantId },
        body: { file: file as unknown as string },
      });

      if (!result) {
        throw new Error("Upload failed - no response from server");
      }

      return result;
    },
    onSuccess: (_data, variables) => {
      toast.success("Background image uploaded successfully");
      invalidate(variables.tenantId, variables.deploymentCell);
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to upload background. Please try again.";
      toast.error(errorMessage);
      console.error("Upload background error:", error);
    },
  });
};
